import { Component, ElementRef, HostListener, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService, SortEvent } from 'primeng/api';
import { Subscription } from 'rxjs';
import { PessoaService } from '../../../services/pessoas/pessoas.service';
import { TableColumn } from '../../../models/shared/table-column.model';

@Component({
  selector: 'app-lista-de-fornecedores',
  templateUrl: './lista-de-fornecedores.component.html',
  styleUrls: ['./lista-de-fornecedores.component.scss'],
  providers: [MessageService],
})
export class ListaDeFornecedoresComponent implements OnInit, OnDestroy {
  // ViewChild para foco no campo de busca (F2)
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

  deleteDialogVisible = false;
  deleteItemId: string | null = null;

  // Objetos de traducao (padrao nova-despesa)
  texts = {
    titulo: '',
    subtitulo: '',
    lista_de_fornecedores: '',
    nenhum_fornecedor: '',
    ajuste_filtros: '',
    carregando: '',
    confirmar_exclusao: '',
    confirmacao: '',
    fornecedor_excluido_sucesso: '',
    erro_ao_excluir_fornecedor: ''
  };

  labels = {
    codigo_fornecedor: '',
    nome: '',
    nome_fantasia: '',
    telefone: '',
    ci: '',
    ruc: '',
    cidade: '',
    data_inclusao: '',
    acoes: '',
    itens_por_pagina: ''
  };

  placeholders = {
    pesquisar: ''
  };

  botoes = {
    adicionar_fornecedor: '',
    confirmar: '',
    cancelar: ''
  };

  tooltips = {
    visualizar: '',
    editar: '',
    excluir: ''
  };

  private langChangeSub!: Subscription;

  // Dados da tabela
  pessoas: any[] = [];
  first3: number = 0;
  rows3: number = 10;
  totalRecords: number = 0;
  loading: boolean = true;
  searchNome: string = '';
  lastfield = 'id';
  lastSort = 'asc';

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  // Configuracao de colunas visiveis (padrao nova-despesa)
  readonly STORAGE_KEY = 'lista-de-fornecedores-visible-columns';

  columns: TableColumn[] = [
    { key: 'codigoPessoa', labelKey: 'lista-de-fornecedores.labels.codigo_fornecedor', visible: true, fixed: true, defaultVisible: true },
    { key: 'nome', labelKey: 'lista-de-fornecedores.labels.nome', visible: true, fixed: false, defaultVisible: true },
    { key: 'fantasia', labelKey: 'lista-de-fornecedores.labels.nome_fantasia', visible: true, fixed: false, defaultVisible: true },
    { key: 'telefone', labelKey: 'lista-de-fornecedores.labels.telefone', visible: true, fixed: false, defaultVisible: true },
    { key: 'ci', labelKey: 'lista-de-fornecedores.labels.ci', visible: true, fixed: false, defaultVisible: true },
    { key: 'ruc', labelKey: 'lista-de-fornecedores.labels.ruc', visible: true, fixed: false, defaultVisible: true },
    { key: 'cidade', labelKey: 'lista-de-fornecedores.labels.cidade', visible: true, fixed: false, defaultVisible: true },
    { key: 'criadoEm', labelKey: 'lista-de-fornecedores.labels.data_inclusao', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'lista-de-fornecedores.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  constructor(
    private messageService: MessageService,
    private pessoaService: PessoaService,
    private router: Router,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    this.loadColumnPreferences();
    this.findFornecedores();
    this.loadTranslations();

    // Atualiza traducoes quando idioma muda
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('lista-de-fornecedores.texts.titulo'),
      subtitulo: this.translate.instant('lista-de-fornecedores.texts.subtitulo'),
      lista_de_fornecedores: this.translate.instant('lista-de-fornecedores.texts.lista_de_fornecedores'),
      nenhum_fornecedor: this.translate.instant('lista-de-fornecedores.texts.nenhum_fornecedor'),
      ajuste_filtros: this.translate.instant('lista-de-fornecedores.texts.ajuste_filtros'),
      carregando: this.translate.instant('lista-de-fornecedores.texts.carregando'),
      confirmar_exclusao: this.translate.instant('lista-de-fornecedores.texts.confirmar_exclusao'),
      confirmacao: this.translate.instant('lista-de-fornecedores.texts.confirmacao'),
      fornecedor_excluido_sucesso: this.translate.instant('lista-de-fornecedores.texts.fornecedor_excluido_sucesso'),
      erro_ao_excluir_fornecedor: this.translate.instant('lista-de-fornecedores.texts.erro_ao_excluir_fornecedor')
    };

    this.labels = {
      codigo_fornecedor: this.translate.instant('lista-de-fornecedores.labels.codigo_fornecedor'),
      nome: this.translate.instant('lista-de-fornecedores.labels.nome'),
      nome_fantasia: this.translate.instant('lista-de-fornecedores.labels.nome_fantasia'),
      telefone: this.translate.instant('lista-de-fornecedores.labels.telefone'),
      ci: this.translate.instant('lista-de-fornecedores.labels.ci'),
      ruc: this.translate.instant('lista-de-fornecedores.labels.ruc'),
      cidade: this.translate.instant('lista-de-fornecedores.labels.cidade'),
      data_inclusao: this.translate.instant('lista-de-fornecedores.labels.data_inclusao'),
      acoes: this.translate.instant('lista-de-fornecedores.labels.acoes'),
      itens_por_pagina: this.translate.instant('lista-de-fornecedores.labels.itens_por_pagina')
    };

    this.placeholders = {
      pesquisar: this.translate.instant('lista-de-fornecedores.placeholders.pesquisar')
    };

    this.botoes = {
      adicionar_fornecedor: this.translate.instant('lista-de-fornecedores.botoes.adicionar_fornecedor'),
      confirmar: this.translate.instant('lista-de-fornecedores.botoes.confirmar'),
      cancelar: this.translate.instant('lista-de-fornecedores.botoes.cancelar')
    };

    this.tooltips = {
      visualizar: this.translate.instant('lista-de-fornecedores.tooltips.visualizar'),
      editar: this.translate.instant('lista-de-fornecedores.tooltips.editar'),
      excluir: this.translate.instant('lista-de-fornecedores.tooltips.excluir')
    };
  }

  /** Verifica se uma coluna esta visivel */
  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  /** Quantidade de colunas visiveis (para colspan) */
  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  /** Handler para mudanca de colunas vindas do ColumnSelector */
  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  /** Carrega preferencias de colunas do localStorage */
  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferencias de colunas:', e);
    }
  }

  findFornecedores(
    page: number = 0,
    size: number = this.rows3,
    sort: string = 'id,asc',
  ) {
    this.loading = true;
    let filter = `categoriaFornecedor=true`;

    if (this.searchNome && this.searchNome.trim() !== '') {
      const search = encodeURIComponent(this.searchNome.trim());

      filter += `&email=${search}`;
      filter += `&nome=${search}`;
      filter += `&fantasia=${search}`;
      filter += `&ruc=${search}`;
      filter += `&telefone=${search}`;
      filter += `&ci=${search}`;
      filter += `&nomeCidade=${search}`;

      if (!isNaN(Number(search))) {
        filter += `&codigoPessoa=${search}`;
      }
    }

    this.pessoaService
      .get('/pessoas/buscar', {
        page,
        size,
        sort,
        filter: filter,
      }).subscribe({
        next: (response) => {
          this.pessoas = response.content;
          this.totalRecords = response.totalElements;
          this.first3 = page * size;
          this.loading = false;
        },
        error: (error) => {
          console.error('Erro ao buscar fornecedores:', error);
          this.pessoas = [];
          this.totalRecords = 0;
          this.loading = false;
        }
      });
  }

  onPageChange3(event: any) {
    const page = event.page;
    const size = event.rows;
    this.findFornecedores(page, size);
  }

  onRowsPerPageChange() {
    this.first3 = 0;
    this.findFornecedores(0, this.rows3, `${this.lastfield},${this.lastSort}`);
  }

  customSort(event: SortEvent) {
    if (!event.field || !event.data) return;

    const field = event.field;
    const order = event.order ?? 1;

    // Atualiza lastfield/lastSort para manter ordenacao em buscas futuras
    this.lastfield = field;
    this.lastSort = order === 1 ? 'asc' : 'desc';

    event.data.sort((a: any, b: any) => {
      const valueA = this.getSortValue(a, field);
      const valueB = this.getSortValue(b, field);

      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return -1 * order;
      if (valueB == null) return 1 * order;

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return valueA.localeCompare(valueB) * order;
      }

      return (valueA < valueB ? -1 : valueA > valueB ? 1 : 0) * order;
    });
  }

  private getSortValue(item: any, field: string): any {
    const parts = field.split('.');
    let value: any = item;
    for (const part of parts) {
      if (value == null) return null;
      value = value[part];
    }
    if (value != null && typeof value === 'object' && 'nome' in value) {
      return value.nome;
    }
    return value;
  }

  onSearchChange(value: string) {
    this.searchNome = value || '';
    if (this.searchNome.length === 0 || this.searchNome.length >= 3) {
      this.search();
    }
  }

  editarFornecedor(id: string) {
    this.router.navigate(['/fornecedor/edit', id]);
  }

  deletarFornecedor(id: string) {
    this.deleteItemId = id;
    this.deleteDialogVisible = true;
  }

  confirmarExclusao() {
    if (!this.deleteItemId) return;
    this.pessoaService.delete(`/pessoas/${this.deleteItemId}`).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.texts.confirmacao,
          detail: this.texts.fornecedor_excluido_sucesso,
        });
        this.search();
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
      },
      error: (err: any) => {
        const detalhe = err?.error?.errors?.[0]?.mensagem
          || err?.error?.message
          || this.texts.erro_ao_excluir_fornecedor;
        this.messageService.add({
          severity: 'error',
          summary: this.texts.confirmacao,
          detail: detalhe,
        });
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
      },
    });
  }

  search() {
    this.findFornecedores(
      0,
      this.rows3,
      `${this.lastfield},${this.lastSort}`
    );
  }

  viewCliente(id: string) {
    this.router.navigate(['/lista-de-fornecedores/view', id]);
  }

  formatarDataEHora(dataISO: string): string {
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) {
      return '';
    }
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  // Atalhos de teclado
  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(ev: KeyboardEvent): void {
    // F2: focar no input de busca
    if (ev.key === 'F2') {
      ev.preventDefault();
      setTimeout(() => {
        this.searchInputRef?.nativeElement?.focus();
      }, 100);
      return;
    }

    // Ctrl+Enter: navegar para adicionar fornecedor
    if (ev.ctrlKey && ev.key === 'Enter') {
      const activeEl = document.activeElement;
      if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA')) return;

      ev.preventDefault();
      this.router.navigate(['/adicionar-fornecedor']);
      return;
    }
  }
}
